import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ArrowRight, Megaphone, Bell, Store, Heart, Briefcase, Users, BarChart3 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { CategoryIcon } from "../components/CategoryIcon";
import { SectionHeading } from "../components/Motifs";
import { Reveal } from "../components/Reveal";
import ListingCard from "../components/ListingCard";
import AdSlot from "../components/AdSlot";

const HERO_IMG = "https://images.pexels.com/photos/18783419/pexels-photo-18783419.jpeg?auto=compress&cs=tinysrgb&w=1920";

const TILES = [
  { slug: "restaurants", icon: "Utensils", key: "cat.restaurants" },
  { slug: "marketplace", icon: "ShoppingBag", key: "tile.grocery" },
  { slug: "professionals", icon: "Briefcase", key: "tile.professional" },
  { slug: "real_estate", icon: "Home", key: "cat.real_estate" },
  { slug: "health", icon: "HeartPulse", key: "tile.healthbeauty" },
  { slug: "education", icon: "GraduationCap", key: "cat.education" },
  { slug: "auto", icon: "Car", key: "tile.automotive" },
  { slug: "jobs", icon: "UserCheck", key: "cat.jobs" },
  { slug: "events", icon: "Calendar", key: "cat.events" },
  { slug: "community", icon: "Users", key: "cat.community" },
];

const TRUST = [
  { icon: Store, key: "trust.1" },
  { icon: Heart, key: "trust.2" },
  { icon: Briefcase, key: "trust.3" },
  { icon: Users, key: "trust.4" },
];

function MaskedLine({ children, delay, className = "" }) {
  return (
    <span className={`mask-line ${className}`}>
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function CategoryRow({ slug, label, icon, items }) {
  const { t, tOr } = useLang();
  if (!items || items.length === 0) return null;
  return (
    <section className="py-16 lg:py-20" data-testid={`home-section-${slug}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex items-end justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center text-gold">
              <CategoryIcon name={icon} className="w-5 h-5" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark">{t("sec.fresh")}</p>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-navy">{tOr(`cat.${slug}`, label)}</h2>
            </div>
          </div>
          <Link
            to={`/category/${slug}`}
            data-testid={`view-all-${slug}`}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-navy-royal hover:text-gold-dark transition-colors"
          >
            {t("sec.viewall")} <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="flex gap-6 overflow-x-auto pb-4 snap-x scrollbar-none lg:grid lg:grid-cols-4 lg:overflow-visible">
          {items.map((l, i) => (
            <div key={l.id} className="min-w-[280px] sm:min-w-[320px] lg:min-w-0 snap-start">
              <Reveal delay={i * 0.07} className="h-full">
                <ListingCard listing={l} />
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, tOr, lang } = useLang();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const settings = data?.settings;
  const categories = data?.categories || [];
  const activeCountries = (data?.countries || []).filter((c) => c.status === "active");
  const catMeta = Object.fromEntries(categories.map((c) => [c.slug, c]));

  const doSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    const term = q.trim();
    const place = loc.trim();
    let matched = false;
    if (place) {
      const lower = place.toLowerCase();
      for (const c of activeCountries) {
        if (matched) break;
        for (const s of c.states || []) {
          if (matched) break;
          for (const ci of s.cities || []) {
            if (ci.toLowerCase() === lower || (lower.length >= 3 && ci.toLowerCase().startsWith(lower))) {
              params.set("city", ci);
              params.set("state", s.name);
              params.set("country", c.code);
              matched = true;
              break;
            }
          }
          if (!matched && s.name.toLowerCase() === lower) {
            params.set("state", s.name);
            params.set("country", c.code);
            matched = true;
          }
        }
      }
    }
    if (term) params.set("q", term);
    if (place && !matched && !term) params.set("q", place);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      {/* ---------- HERO — cinematic navy + gold ---------- */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <img
          src={HERO_IMG}
          alt="Shwedagon Pagoda glowing at night over the Yangon skyline"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-navy/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 lg:pt-24 lg:pb-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-xs font-mono font-medium uppercase tracking-[0.25em] text-gold mb-5"
            data-testid="hero-overline"
          >
            <img src="/bh-icon-180.png" alt="" className="w-5 h-5 rounded-full" />
            {lang === "my" ? t("hero.overline") : (settings?.hero_overline || t("hero.overline"))}
          </motion.p>
          <h1 className="font-serif font-bold tracking-tight leading-[1.1] text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="hero-headline">
            <MaskedLine delay={0.05} className="!inline-block">{t("hero.find")}</MaskedLine>{" "}
            <MaskedLine delay={0.2} className="!inline-block">{t("hero.connect")}</MaskedLine>{" "}
            <MaskedLine delay={0.35} className="!inline-block"><span className="italic text-gold">{t("hero.belong")}</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl"
            data-testid="hero-subtext"
          >
            {lang === "my" ? t("hero.sub.spec") : (settings?.sub_tagline || t("hero.sub.spec"))}
          </motion.p>

          <motion.form
            onSubmit={doSearch}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.6 }}
            className="mt-9 max-w-4xl bg-white p-3 md:p-4 rounded-2xl shadow-2xl grid grid-cols-1 md:grid-cols-12 gap-3"
            data-testid="hero-search-form"
          >
            <div className="md:col-span-5 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-cream/70 border border-[#E2E8F0]">
              <Search className="w-5 h-5 text-stone2 shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("hero.kw.ph")}
                className="bg-transparent w-full text-sm text-ink outline-none placeholder:text-stone2"
                data-testid="hero-search-input"
              />
            </div>
            <div className="md:col-span-4 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-cream/70 border border-[#E2E8F0]">
              <MapPin className="w-5 h-5 text-stone2 shrink-0" />
              <input
                value={loc}
                onChange={(e) => setLoc(e.target.value)}
                placeholder={t("hero.loc.ph")}
                className="bg-transparent w-full text-sm text-ink outline-none placeholder:text-stone2"
                data-testid="hero-location-input"
              />
            </div>
            <button
              type="submit"
              data-testid="hero-search-submit"
              className="md:col-span-3 bg-gold hover:bg-gold-dark text-navy font-bold text-sm px-6 py-3.5 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2 shadow-md"
            >
              <Search className="w-4 h-4" /> {t("hero.search")}
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-7 flex flex-wrap gap-x-7 gap-y-3"
            data-testid="trust-chips"
          >
            {TRUST.map((chip, i) => (
              <span key={chip.key} className="flex items-center gap-2 text-xs sm:text-sm text-white/75" data-testid={`trust-chip-${i}`}>
                <chip.icon className="w-4 h-4 text-gold" />
                {t(chip.key)}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------- CATEGORY TILES ---------- */}
      <section className="py-16 lg:py-24 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            overline={t("sec.categories.o")}
            title={t("sec.categories.t")}
            sub={t("sec.categories.sub")}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-testid="categories-grid">
            {TILES.map((tile, i) => {
              const count = catMeta[tile.slug]?.count ?? 0;
              return (
                <Reveal key={tile.slug} delay={i * 0.04}>
                  <Link
                    to={`/category/${tile.slug}`}
                    data-testid={`category-card-${tile.slug}`}
                    className="group bg-cream/60 rounded-xl border border-[#E2E8F0] p-5 sm:p-6 hover:border-gold hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center"
                  >
                    <span className="w-12 h-12 rounded-full bg-white border border-[#E2E8F0] group-hover:border-gold group-hover:bg-gold-light flex items-center justify-center text-navy-royal group-hover:text-gold-dark transition-colors duration-300">
                      <CategoryIcon name={tile.icon} className="w-5 h-5" />
                    </span>
                    <p className="mt-3.5 font-sans font-semibold text-navy text-sm sm:text-base group-hover:text-navy-royal transition-colors">{t(tile.key)}</p>
                    <p className="text-[11px] text-stone2 mt-1 font-mono">{count} {t("common.listings")}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- FEATURED ---------- */}
      {data?.featured?.length > 0 && (
        <section className="py-16 lg:py-24 bg-cream" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-sans uppercase font-bold tracking-[0.2em] text-gold-dark mb-3">{t("sec.featured.o")}</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy tracking-tight">{t("sec.featured.t")}</h2>
              </div>
              <Link
                to="/directory"
                data-testid="view-all-businesses"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark hover:text-navy transition-colors"
              >
                {t("common.viewallbiz")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {data.featured.slice(0, 4).map((l, i) => (
                <Reveal key={l.id} delay={i * 0.07} className="h-full">
                  <ListingCard listing={l} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- AD SLOT ---------- */}
      <section className="py-12 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdSlot placement="homepage_top" />
        </div>
      </section>

      {/* ---------- LATEST ROWS ---------- */}
      <CategoryRow slug="restaurants" label="Restaurants" icon={catMeta.restaurants?.icon || "Utensils"} items={data?.latest?.restaurants} />
      <div className="bg-white">
        <CategoryRow slug="jobs" label="Jobs" icon={catMeta.jobs?.icon || "Briefcase"} items={data?.latest?.jobs} />
      </div>
      <CategoryRow slug="housing" label="Housing" icon={catMeta.housing?.icon || "Home"} items={data?.latest?.housing} />
      <div className="bg-white">
        <CategoryRow slug="events" label="Events" icon={catMeta.events?.icon || "Calendar"} items={data?.latest?.events} />
      </div>

      {/* ---------- MANIFESTO — midnight navy ---------- */}
      <section className="py-20 lg:py-28 bg-navy acheik-bg-dark" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">{t("sec.promise.o")}</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("sec.promise.t")}</h2>
            <div className="gold-hairline w-40 mx-auto mt-6" />
            <p className="mt-6 text-white/60 text-sm sm:text-base max-w-xl mx-auto">{t("sec.promise.sub")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { n: "01", tKey: "promise.1.t", dKey: "promise.1.d" },
              { n: "02", tKey: "promise.2.t", dKey: "promise.2.d" },
              { n: "03", tKey: "promise.3.t", dKey: "promise.3.d" },
            ].map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1} className="h-full">
                <div className="bg-teak-surface p-8 lg:p-10 rounded-2xl border border-teak-line hover:border-gold/40 transition-colors duration-300 h-full" data-testid={`manifesto-chapter-${c.n}`}>
                  <p className="text-4xl sm:text-5xl font-bold text-gold/70 font-mono mb-5">{c.n}</p>
                  <h3 className="font-serif text-2xl font-bold text-white mb-3">{t(c.tKey)}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{t(c.dKey)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ANNOUNCEMENTS ---------- */}
      {settings?.announcements?.length > 0 && (
        <section className="py-14 bg-white border-b border-[#E2E8F0]" data-testid="announcements-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Bell className="w-5 h-5 text-gold-dark" />
              <h2 className="font-serif text-xl sm:text-2xl font-bold text-navy">{t("sec.announce")}</h2>
            </div>
            <div className="space-y-3">
              {settings.announcements.map((a, i) => {
                const text = typeof a === "string" ? a : (lang === "my" && a.my ? a.my : a.en);
                if (!text) return null;
                return (
                  <Reveal key={i} delay={i * 0.06}>
                    <p className="flex items-start gap-3 text-sm text-clay bg-cream/60 border border-[#E2E8F0] rounded-xl px-5 py-4" data-testid={`announcement-${i}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 shrink-0" />
                      {text}
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ---------- GROW CTA — navy panel with gold ---------- */}
      <section className="py-16 lg:py-24 bg-cream" data-testid="advertise-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-navy via-navy-royal to-navy rounded-2xl p-8 md:p-14 border border-gold/30 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 acheik-bg-dark opacity-60" />
            <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <p className="flex items-center gap-2 text-xs font-mono font-medium uppercase tracking-[0.22em] text-gold mb-5">
                  <Megaphone className="w-4 h-4" /> {t("adv.grow")}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                  {t("cta.grow.t")}
                </h2>
                <p className="mt-5 text-white/70 leading-relaxed max-w-lg">
                  {t("cta.grow.s")}
                </p>
                <div className="mt-8 grid sm:grid-cols-3 gap-5">
                  {[
                    { icon: BarChart3, key: "vp.1" },
                    { icon: Users, key: "vp.2" },
                    { icon: Megaphone, key: "vp.3" },
                  ].map((v) => (
                    <div key={v.key} className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-lg bg-gold/15 border border-gold/30 flex items-center justify-center text-gold shrink-0">
                        <v.icon className="w-4 h-4" />
                      </span>
                      <p className="text-xs sm:text-sm text-white/80 leading-snug">{t(v.key)}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-5 flex flex-col items-stretch lg:items-end gap-4">
                <Link
                  to="/submit"
                  data-testid="cta-submit-listing-btn"
                  className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-8 py-4 rounded-xl shadow-lg transition-colors duration-200"
                >
                  {t("cta.list")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/advertise"
                  data-testid="cta-advertise-btn"
                  className="text-center lg:text-right text-sm font-semibold text-gold hover:text-white transition-colors"
                >
                  {t("cta.advertise")} →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
